/**
 * Public invite ACCEPT — ``/connect/invite/$token`` (no auth).
 *
 * The landing page for an Ando Connect magic-link. It previews WHO invited the
 * visitor and to WHICH space, then — on an explicit primary action — accepts
 * the invite, persists the returned space-scoped guest credential, and forwards
 * the visitor into the branded guest Space view (``/connect/space/$spaceId``).
 *
 * Standalone (chrome-less) layout: it renders outside the authenticated
 * workspace shell — mirroring ``/forms/$flowId`` + ``apps/web`` ``/share`` — so
 * it is shareable with people who have no Ando account. Expired / revoked /
 * not-found all resolve to a CALM explanatory card (never a raw error).
 */

import {
  ConnectFocusHeading,
  ConnectPublicLoading,
  ConnectPublicNotice,
  ConnectPublicPage,
  writeGuestCredential,
} from "@/features/connect";
import {
  type ConnectInvitePreview,
  ConnectPublicError,
  acceptInvite,
  getInvitePreview,
} from "@ando/core";
import { cn } from "@ando/ui";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowRight, Building2, ShieldCheck } from "lucide-react";
import type * as React from "react";

export const Route = createFileRoute("/connect/invite/$token")({
  component: ConnectInviteAccept,
});

function reasonOf(err: unknown): ConnectPublicError | null {
  return err instanceof ConnectPublicError ? err : null;
}

function ConnectInviteAccept(): React.JSX.Element {
  const { i18n } = useLingui();
  const navigate = useNavigate();
  const { token } = Route.useParams();

  const preview = useQuery<ConnectInvitePreview, ConnectPublicError>({
    queryKey: ["connect-invite-preview", token],
    queryFn: () => getInvitePreview(token),
    retry: false,
  });

  const accept = useMutation({
    mutationFn: () => acceptInvite(token),
    onSuccess: (res) => {
      // Persist the SPACE-SCOPED guest credential, then enter the space.
      writeGuestCredential(res);
      void navigate({
        to: "/connect/space/$spaceId",
        params: { spaceId: res.space_id },
        replace: true,
      });
    },
  });

  // ── Loading ──────────────────────────────────────────────────────────
  if (preview.isLoading) {
    return <ConnectPublicLoading />;
  }

  // ── Preview failure → calm explanatory card ──────────────────────────
  const previewErr = reasonOf(preview.error);
  const acceptErr = reasonOf(accept.error);
  if (preview.isError && previewErr) {
    return <InviteFailure error={previewErr} />;
  }

  const data = preview.data;
  if (!data) {
    return (
      <ConnectPublicNotice
        reason="notfound"
        title={<Trans>Invito non trovato</Trans>}
        body={
          <Trans>Questo invito non esiste più. Chiedi a chi te l'ha condiviso un nuovo link.</Trans>
        }
      />
    );
  }

  const orgName = data.space.name?.trim() || i18n._(msg`uno spazio condiviso`);
  const roleLabel =
    data.role === "editor"
      ? i18n._(msg`Collaboratore`)
      : data.role === "viewer"
        ? i18n._(msg`Visualizzatore`)
        : null;

  return (
    <ConnectPublicPage label={i18n._(msg`Invito a uno spazio Ando Connect`)}>
      <div className="m-auto w-full max-w-md">
        <div
          className={cn(
            "flex flex-col items-center gap-4 rounded-[18px] border border-hairline bg-white/70 px-6 py-9 text-center backdrop-blur-xl",
            "shadow-[0_1px_2px_rgba(0,0,0,0.04),inset_0_1px_0_rgba(255,255,255,0.6)] motion-safe:animate-surface-in",
          )}
        >
          <span
            aria-hidden
            className="flex h-12 w-12 items-center justify-center rounded-2xl bg-ando-orange-soft text-ando-orange"
          >
            <Building2 size={22} strokeWidth={1.75} />
          </span>

          <div className="flex flex-col gap-1.5">
            <p className="font-medium text-[12.5px] text-ink-2">
              <Trans>Sei stato invitato a collaborare in</Trans>
            </p>
            <ConnectFocusHeading className="text-[22px]">{orgName}</ConnectFocusHeading>
            {roleLabel ? (
              <span className="mx-auto inline-flex items-center gap-1.5 rounded-full bg-cream-deep/80 px-2.5 py-1 font-medium text-[11.5px] text-ink-2">
                <ShieldCheck size={12} strokeWidth={2.1} aria-hidden />
                {roleLabel}
              </span>
            ) : null}
          </div>

          <p className="max-w-xs text-[12.5px] text-ink-2 leading-relaxed">
            <Trans>
              Accettando potrai fare domande allo spazio. Ando risponde solo dalle fonti condivise
              con te — niente di più.
            </Trans>
          </p>

          {acceptErr ? (
            <p className="text-[12px] text-rose-500" role="alert">
              {acceptErr.reason === "expired" ? (
                <Trans>Questo invito è scaduto.</Trans>
              ) : acceptErr.reason === "gone" ? (
                <Trans>Questo invito non è più valido.</Trans>
              ) : (
                <Trans>Non siamo riusciti ad accettare l'invito. Riprova.</Trans>
              )}
            </p>
          ) : null}

          <button
            type="button"
            onClick={() => accept.mutate()}
            disabled={accept.isPending}
            className={cn(
              "mt-1 inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 font-medium text-[14px] text-white transition-all",
              "bg-ando-orange hover:bg-ando-orange/90 disabled:opacity-50",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/55 focus-visible:ring-offset-2 focus-visible:ring-offset-cream",
            )}
          >
            {accept.isPending ? (
              <Trans>Apertura…</Trans>
            ) : (
              <>
                <Trans>Accetta e apri lo spazio</Trans>
                <ArrowRight size={16} strokeWidth={2} aria-hidden />
              </>
            )}
          </button>

          <p className="text-[11px] text-ink-2">
            <Trans>Accesso in sola lettura · solo le fonti condivise</Trans>
          </p>
        </div>
      </div>
    </ConnectPublicPage>
  );
}

/** Maps a preview ``ConnectPublicError`` onto the shared calm notice. */
function InviteFailure({ error }: { readonly error: ConnectPublicError }): React.JSX.Element {
  if (error.reason === "expired") {
    return (
      <ConnectPublicNotice
        reason="expired"
        title={<Trans>Invito scaduto</Trans>}
        body={
          <Trans>
            Questo link di invito è scaduto. Chiedi a chi te l'ha condiviso di inviartene uno nuovo.
          </Trans>
        }
      />
    );
  }
  if (error.reason === "gone") {
    return (
      <ConnectPublicNotice
        reason="gone"
        title={<Trans>Invito non più valido</Trans>}
        body={
          <Trans>
            Questo invito è stato revocato o è già stato usato. Chiedi un nuovo link per accedere
            allo spazio.
          </Trans>
        }
      />
    );
  }
  if (error.reason === "rate_limited") {
    return (
      <ConnectPublicNotice
        reason="rate_limited"
        title={<Trans>Troppi tentativi</Trans>}
        body={<Trans>Hai fatto troppe richieste. Attendi qualche istante e riprova.</Trans>}
      />
    );
  }
  return (
    <ConnectPublicNotice
      reason={error.reason === "notfound" ? "notfound" : "error"}
      title={
        error.reason === "notfound" ? (
          <Trans>Invito non trovato</Trans>
        ) : (
          <Trans>Qualcosa è andato storto</Trans>
        )
      }
      body={
        error.reason === "notfound" ? (
          <Trans>Questo invito non esiste. Verifica il link o chiedine uno nuovo.</Trans>
        ) : (
          <Trans>Non siamo riusciti a caricare l'invito. Riprova tra poco.</Trans>
        )
      }
    />
  );
}
